/**
 * Detects the `e2e_test_mode=true` URL param so client code can switch into
 * deterministic, E2E-friendly behavior. Used to:
 *
 *  - Render dates as a placeholder (`component/date.tsx`).
 *  - Disable post-action survey popovers (`feature/surveys/*`).
 *
 * Reads sessionStorage first (so the flag survives same-origin redirects
 * that drop the param), falls back to the URL search string, and caches the
 * result in a module-level variable.
 *
 * The flag is intentionally a single global rather than per-feature switches
 * so anything that should be deterministic in screenshots can opt in via one
 * check. Don't add UI-relevant behavior changes here — only deterministic-
 * for-tests changes.
 */

const STORAGE_KEY = 'e2e_test_mode';
let cached: boolean | undefined;

export function isE2eTestMode(): boolean {
    if (cached !== undefined) return cached;

    try {
        if (typeof window === 'undefined') {
            cached = false;
            return cached;
        }
        if (window.sessionStorage?.getItem(STORAGE_KEY) === 'true') {
            cached = true;
            return cached;
        }
        const params = new URLSearchParams(window.location.search);
        if (params.get(STORAGE_KEY) === 'true') {
            window.sessionStorage?.setItem(STORAGE_KEY, 'true');
            cached = true;
            return cached;
        }
    } catch {
        // Defensive: any storage/URL access failure means the flag is off.
    }
    cached = false;
    return cached;
}

/** Test-only: clear the module cache. Not for production use. */
export function __resetE2eTestModeCache(): void {
    cached = undefined;
}
